/* eslint-disable max-lines */
import { FocusMonitor } from '@angular/cdk/a11y';
import { CdkAccordionItem } from '@angular/cdk/accordion';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { HxIcon } from '../icon/icon';
import { HxAccordion } from './accordion';

@Component({
  selector: 'hx-accordion-panel',
  standalone: true,
  imports: [HxIcon],
  templateUrl: './accordion-panel.html',
  styleUrl: './accordion.css',
  host: {
    '[class]': `'hx-accordion-panel ' + (customCssClass() || '')`,
    '[class.hx-accordion-panel--expanded]': 'expanded',
    '[class.hx-accordion-panel--disabled]': 'disabled',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxAccordionPanel extends CdkAccordionItem implements AfterViewInit, OnDestroy {
  private static nextId = 0;
  private readonly _elementRef = inject(ElementRef);
  private readonly _focusMonitor = inject(FocusMonitor);
  protected readonly parentAccordion = inject(HxAccordion, { optional: true });

  /**
   * Whether to hide the toggle indicator (chevron icon). When true, the header remains clickable but the icon is
   * hidden.
   *
   * @default false
   */
  readonly hideToggle = input(false, { transform: booleanAttribute });

  /**
   * Size of the panel header label text.
   *
   * @default 'medium'
   */
  readonly labelSize = input<'small' | 'medium' | 'large'>('medium');
  readonly customCssClass = input<string>('');
  /**
   * Icon name to display in the panel header, before the label text. Must be a valid icon name from the Harmony icon
   * library (hx-icon component). Optional - only render when provided.
   *
   * @example
   *   ```html
   *   <hx-accordion-panel headerIcon="settings">
   *     <div hxAccordionHeader>Configuration</div>
   *   </hx-accordion-panel>
   *   ```;
   *
   * @default undefined
   */
  readonly headerIcon = input<string | undefined>();

  /**
   * Whether the control should be shown in the designer.
   *
   * When false, the control is hidden from design tools and template builders.
   *
   * @default true
   */
  readonly showInDesigner = input<boolean>(true);

  /** Event emitted when the panel has finished expanding. */
  @Output() override readonly opened = new EventEmitter<void>();

  /** Event emitted when the panel has finished collapsing. */
  @Output() override readonly closed = new EventEmitter<void>();

  /** Unique IDs for ARIA linkage */
  protected readonly headerId = `hx-accordion-header-${HxAccordionPanel.nextId}`;
  protected readonly contentId = `hx-accordion-content-${HxAccordionPanel.nextId++}`;

  /** Internal state for animation tracking */
  protected readonly isAnimating = signal(false);
  private animationTimer: ReturnType<typeof setTimeout> | null = null;
  private previousExpandedState = false;

  /** Internal signal that tracks the expanded state for reactive icon updates */
  protected readonly expandedSignal = signal(false);

  /**
   * Computed signal that returns the index of this panel within its parent accordion. Used for section numbering when
   * showSectionNumbers is enabled.
   */
  protected readonly panelIndex = computed(() => {
    if (!this.parentAccordion) {
      return -1;
    }
    const panels = this.parentAccordion.panelsSignal();
    return panels.indexOf(this);
  });

  /**
   * Computed signal that returns the current icon name based on panel expanded state. Uses icons from parent accordion
   * if available, falls back to component defaults. Automatically switches between expandedIcon and collapsedIcon with
   * instant timing (0ms).
   *
   * Icon selection logic:
   *
   * - When expanded: returns accordion's expandedIcon() or fallback 'chevron-up'
   * - When collapsed: returns accordion's collapsedIcon() or fallback 'chevron-down'
   * - Empty string inputs trigger fallback to default chevrons
   *
   * This computed property eliminates CSS rotation transforms by swapping icon content directly, resulting in instant
   * icon changes (0ms transition) while preserving panel animation independence.
   *
   * @example
   *   ```typescript
   *   // Component usage in template
   *   <hx-icon [svgIcon]="currentIconName()" />
   *
   *   // Reactivity flow:
   *   // 1. User clicks header
   *   // 2. expanded signal updates
   *   // 3. currentIconName recomputes instantly
   *   // 4. hx-icon re-renders with new icon (0ms)
   *   // 5. Panel animates independently (250ms default)
   *   ```;
   *
   * @returns Icon name string for hx-icon component's [svgIcon] binding
   * @internal Used by accordion-panel.html template
   */
  protected readonly currentIconName = computed(() => {
    const isExpanded = this.expandedSignal();
    const expandIcon = this.parentAccordion?.expandedIcon() || 'chevron-up';
    const collapseIcon = this.parentAccordion?.collapsedIcon() || 'chevron-down';

    if (isExpanded) {
      return expandIcon || 'chevron-up';
    } else {
      return collapseIcon || 'chevron-down';
    }
  });

  constructor() {
    super();
    // Watch for expansion state changes to trigger animations and events
    this._watchExpansionChanges();
  }

  /** Watch for changes to the expanded property and trigger animations */
  private _watchExpansionChanges(): void {
    // Use effect to watch for expansion state changes
    // This runs whenever the expanded property changes (from toggle, open, close)
    const checkExpansion = (): void => {
      const currentExpanded = this.expanded;
      if (currentExpanded !== this.previousExpandedState) {
        this.previousExpandedState = currentExpanded;
        // Update the reactive signal for icon changes
        this.expandedSignal.set(currentExpanded);
        this.handleAnimationStart();
      }
      // Continue watching
      requestAnimationFrame(checkExpansion);
    };
    requestAnimationFrame(checkExpansion);
  }

  ngAfterViewInit(): void {
    // Setup focus monitoring for focus-visible
    this._focusMonitor.monitor(this._elementRef, true);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._focusMonitor.stopMonitoring(this._elementRef);
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }
  }

  /** Toggles the expansion state of the panel. */
  override toggle(): void {
    if (!this.disabled) {
      super.toggle();
    }
  }

  /** Expands the accordion panel. */
  override open(): void {
    if (!this.disabled) {
      super.open();
    }
  }

  /** Collapses the accordion panel. */
  override close(): void {
    if (!this.disabled) {
      super.close();
    }
  }

  /** Handle click on panel header */
  protected onHeaderClick(): void {
    if (!this.disabled) {
      this.toggle();
    }
  }

  /** Handle keyboard events on panel header */
  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    const target = event.target as HTMLElement;
    const isHeader = target.classList.contains('hx-accordion-panel__header');

    if (!isHeader) {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
      case 'Spacebar': // For older browsers
        event.preventDefault();
        this.toggle();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.focusNextPanel();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousPanel();
        break;

      case 'Home':
        event.preventDefault();
        this.focusFirstPanel();
        break;

      case 'End':
        event.preventDefault();
        this.focusLastPanel();
        break;
    }
  }

  /** Focus the next enabled panel */
  private focusNextPanel(): void {
    if (!this.parentAccordion) {
      return;
    }

    const panels = this.parentAccordion.panels.toArray();
    const currentIndex = panels.indexOf(this);

    for (let i = currentIndex + 1; i < panels.length; i++) {
      const panel = panels[i];
      if (panel && !panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }

    // Wrap to first panel if at end
    for (let i = 0; i <= currentIndex; i++) {
      const panel = panels[i];
      if (panel && !panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }
  }

  /** Focus the previous enabled panel */
  private focusPreviousPanel(): void {
    if (!this.parentAccordion) {
      return;
    }

    const panels = this.parentAccordion.panels.toArray();
    const currentIndex = panels.indexOf(this);

    for (let i = currentIndex - 1; i >= 0; i--) {
      const panel = panels[i];
      if (panel && !panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }

    // Wrap to last panel if at beginning
    for (let i = panels.length - 1; i >= currentIndex; i--) {
      const panel = panels[i];
      if (panel && !panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }
  }

  /** Focus the first enabled panel */
  private focusFirstPanel(): void {
    if (!this.parentAccordion) {
      return;
    }

    const panels = this.parentAccordion.panels.toArray();
    for (const panel of panels) {
      if (!panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }
  }

  /** Focus the last enabled panel */
  private focusLastPanel(): void {
    if (!this.parentAccordion) {
      return;
    }

    const panels = this.parentAccordion.panels.toArray();
    for (let i = panels.length - 1; i >= 0; i--) {
      const panel = panels[i];
      if (panel && !panel.disabled) {
        this.focusPanel(panel);
        return;
      }
    }
  }

  /** Focus a specific panel's header */
  private focusPanel(panel: HxAccordionPanel): void {
    const nativeElement = (panel as unknown as { _elementRef: ElementRef })._elementRef.nativeElement as HTMLElement;
    const header = nativeElement.querySelector('.hx-accordion-panel__header') as HTMLElement;
    if (header) {
      header.focus();
    }
  }

  /** Handle animation start and completion */
  private handleAnimationStart(): void {
    const expandedValue = this.expanded;

    // Cancel any ongoing animation
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.isAnimating.set(false);
    }

    // Start new animation
    this.isAnimating.set(true);

    // Use token value for animation duration (default 250ms)
    const duration = this.getAnimationDuration();

    this.animationTimer = setTimeout(() => {
      this.isAnimating.set(false);
      if (expandedValue) {
        this.opened.emit();
      } else {
        this.closed.emit();
      }
      this.animationTimer = null;
    }, duration);
  }

  /** Get animation duration from CSS token */
  private getAnimationDuration(): number {
    try {
      const element = this._elementRef.nativeElement;
      const duration = getComputedStyle(element).getPropertyValue('--hx-accordion-animation-duration');
      const ms = parseFloat(duration);
      return isNaN(ms) ? 250 : ms;
    } catch {
      return 250; // Default fallback
    }
  }
}
